import { Sentence } from 'interfaces/db/sentence'
import { Transcript } from 'interfaces/db/transcript'
import { formatSeconds } from 'util/time'
import { create } from 'zustand'

export interface StoreState {
  includeSpeaker: boolean
  setIncludeSpeaker: (includeSpeaker: boolean) => void
  inlineSpeakers: boolean
  setInlineSpeakers: (inlineSpeakers: boolean) => void
  inlineTimestamps: boolean
  setInlineTimestamps: (inlineTimestamps: boolean) => void
  inlineText: boolean
  setInlineText: (inlineText: boolean) => void
  includeTimestamps: boolean
  setIncludeTimestamps: (includeTimestamps: boolean) => void
  spaceBetweenSpeakers: boolean
  setSpaceBetweenSpeakers: (spaceBetweenSpeakers: boolean) => void
  showSameSpeakerOnce: boolean
  setShowSameSpeakerOnce: (showSameSpeakerOnce: boolean) => void
  format: 'paragraphs' | 'sentences'
  setFormat: (format: 'paragraphs' | 'sentences') => void
  transcript: Transcript | null
  setTranscript: (transcript: Transcript) => void
  exportTranscript: (options?: { sentenceCount?: number }) => string | undefined
}

export const useFormattedExportStore = create<StoreState>((set) => ({
  includeSpeaker: true,
  setIncludeSpeaker: (includeSpeaker: boolean) => set({ includeSpeaker }),
  inlineSpeakers: false,
  setInlineSpeakers: (inlineSpeakers: boolean) => set({ inlineSpeakers }),
  inlineTimestamps: false,
  setInlineTimestamps: (inlineTimestamps: boolean) => set({ inlineTimestamps }),
  inlineText: false,
  setInlineText: (inlineText: boolean) => set({ inlineText }),
  includeTimestamps: true,
  setIncludeTimestamps: (includeTimestamps: boolean) =>
    set({ includeTimestamps }),
  spaceBetweenSpeakers: true,
  setSpaceBetweenSpeakers: (spaceBetweenSpeakers: boolean) =>
    set({ spaceBetweenSpeakers }),
  showSameSpeakerOnce: true,
  setShowSameSpeakerOnce: (showSameSpeakerOnce: boolean) =>
    set({ showSameSpeakerOnce }),
  format: 'paragraphs',
  setFormat: (format: 'paragraphs' | 'sentences') => set({ format }),
  transcript: null as Transcript | null,
  setTranscript: (transcript: Transcript) => set({ transcript }),
  exportTranscript: function ({ sentenceCount = -1 } = {}): string | undefined {
    const {
      transcript,
      format,
      includeSpeaker,
      inlineSpeakers,
      inlineTimestamps,
      inlineText,
      spaceBetweenSpeakers,
      includeTimestamps,
      showSameSpeakerOnce,
    } = useFormattedExportStore.getState()

    if (!transcript || !transcript.sentences) {
      console.error('Invalid transcript data')
      return undefined
    }

    let sentences = transcript.sentences
    if (sentenceCount > 0) {
      sentences = sentences.slice(0, sentenceCount)
    }

    const maxSentences = showSameSpeakerOnce ? Number.MAX_SAFE_INTEGER : 5

    return format === 'paragraphs'
      ? exportAsParagraphs({
          sentences,
          includeSpeaker,
          inlineSpeakers,
          inlineTimestamps,
          inlineText,
          spaceBetweenSpeakers,
          includeTimestamps,
          maxSentences,
        })
      : exportAsSentences({
          sentences,
          includeSpeaker,
          inlineSpeakers,
          inlineTimestamps,
          inlineText,
          spaceBetweenSpeakers,
          includeTimestamps,
        })
  },
}))

function formatSpeaker(speaker: string, inline: boolean): string {
  return inline ? `${speaker}: ` : `${speaker}:\n`
}

function formatTimestamp(start: number, end: number, inline: boolean): string {
  return inline
    ? `[${formatSeconds(start)} - ${formatSeconds(end)}] `
    : `[${formatSeconds(start)} - ${formatSeconds(end)}]\n`
}
function exportAsParagraphs({
  sentences,
  includeSpeaker,
  inlineSpeakers,
  inlineTimestamps,
  inlineText,
  spaceBetweenSpeakers,
  includeTimestamps,
  maxSentences,
}: {
  sentences: Sentence[]
  includeSpeaker: boolean
  inlineSpeakers: boolean
  inlineTimestamps: boolean
  inlineText: boolean
  spaceBetweenSpeakers: boolean
  includeTimestamps: boolean
  maxSentences: number
}): string {
  let text = ''
  let currentSpeaker = ''
  let paragraphStartTime: number | null = null
  let paragraphEndTime: number | null = null
  let paragraphText = ''
  let sentenceCount = 0

  sentences.forEach((sentence, index) => {
    if (sentence.speaker !== currentSpeaker || sentenceCount >= maxSentences) {
      if (currentSpeaker && paragraphText) {
        // Append the existing paragraph text before starting a new paragraph
        text += appendParagraph(
          paragraphText,
          currentSpeaker,
          paragraphStartTime,
          paragraphEndTime,
          includeSpeaker,
          inlineSpeakers,
          inlineTimestamps,
          inlineText,
          spaceBetweenSpeakers,
          includeTimestamps
        )
      }

      if (sentence.speaker !== currentSpeaker) {
        currentSpeaker = sentence.speaker
        sentenceCount = 0 // Reset sentence count for new speaker
      } else {
        sentenceCount = 0 // Reset sentence count after reaching max sentences
      }

      paragraphStartTime = sentence.start
      paragraphText = ''
    }

    paragraphText += `${sentence.text} `
    paragraphEndTime = sentence.end
    sentenceCount++

    if (index === sentences.length - 1) {
      // Append the last paragraph
      text += appendParagraph(
        paragraphText,
        currentSpeaker,
        paragraphStartTime,
        paragraphEndTime,
        includeSpeaker,
        inlineSpeakers,
        inlineTimestamps,
        inlineText,
        spaceBetweenSpeakers,
        includeTimestamps
      )
    }
  })

  return text.trim()
}

function appendParagraph(
  paragraphText: string,
  currentSpeaker: string,
  paragraphStartTime: number | null,
  paragraphEndTime: number | null,
  includeSpeaker: boolean,
  inlineSpeakers: boolean,
  inlineTimestamps: boolean,
  inlineText: boolean,
  spaceBetweenSpeakers: boolean,
  includeTimestamps: boolean
): string {
  let paragraph = ''

  if (includeSpeaker) {
    paragraph += inlineSpeakers
      ? formatSpeaker(currentSpeaker, true)
      : formatSpeaker(currentSpeaker, false)
  }

  if (
    includeTimestamps &&
    paragraphStartTime !== null &&
    paragraphEndTime !== null
  ) {
    paragraph += inlineTimestamps
      ? formatTimestamp(paragraphStartTime, paragraphEndTime, true)
      : formatTimestamp(paragraphStartTime, paragraphEndTime, false)
  }

  paragraph += inlineText ? paragraphText : paragraphText + '\n'

  if (spaceBetweenSpeakers) {
    paragraph += '\n'
  }

  return paragraph
}

function exportAsSentences({
  sentences,
  includeSpeaker,
  inlineSpeakers,
  inlineTimestamps,
  inlineText,
  includeTimestamps,
  spaceBetweenSpeakers,
}: {
  sentences: Sentence[]
  includeSpeaker: boolean
  inlineSpeakers: boolean
  inlineTimestamps: boolean
  inlineText: boolean
  includeTimestamps: boolean
  spaceBetweenSpeakers: boolean
}): string {
  let text = ''
  let currentSpeaker = ''

  sentences.forEach((sentence, index) => {
    if (sentence.speaker !== currentSpeaker) {
      if (index > 0 && spaceBetweenSpeakers) {
        text += '\n'
      }

      currentSpeaker = sentence.speaker
      if (includeSpeaker) {
        text += inlineSpeakers
          ? formatSpeaker(sentence.speaker, true)
          : formatSpeaker(sentence.speaker, false)
      }
    }

    if (includeTimestamps) {
      text += inlineTimestamps
        ? formatTimestamp(sentence.start, sentence.end, true)
        : formatTimestamp(sentence.start, sentence.end, false)
    }

    text += inlineText ? `${sentence.text} ` : `${sentence.text}\n`
  })

  return text.trim()
}
